import { Reducer } from 'redux'
import {
  ActionTypes,
  WITHDRAW_ACTIONS,
  WithdrawWalletIdType,
  AccountLimits,
  BankAccount,
  WITHDRAW_STEPS,
  FinancialInstituition,
  Account,
  BlockchainAddress,
} from './types'

const initialState = {
  withdrawWalletId: undefined,
  withdrawAccountId: undefined,
  withdrawWalletName: undefined,
  withdrawToBankId: undefined,
  withdrawAmount: '',
  withdrawBankAccount: '',
  isConfirmOpen: false,
  has2FaEnabled: false,
  withdrawStep: WITHDRAW_STEPS.DETAILS,
  is2FaOpen: false,
  isLoading: true,
  accountLimits: {
    withdrawDailyRemaining: '',
    holdingMax: '',
  },
  bankAccounts: [],
  blockchainAddresses: [],
  isSelectedBlockchain: false,
  isAddressLoading: false,
  isErc20FeatureGateLoading: true,
  isZrc2FeatureGateLoading: true,
  isBlockchainWithdrawEnabled: true,
  withdrawAmountError: '',
  withdrawRequestError: '',
  fullyVerified: undefined,
  idMinWithdrawal: '',
  idMaxWithdrawal: '',
  fiList: [],
  accounts: null,
  isErc20Enabled: true,
  isZrc2Enabled: true,
  addressQuota: 0,
  isZrc2NoticeBannerVisible: false,
  isStablecoinFeeLoading: false,
  stablecoinFee: {
    amount: undefined,
    identifier: undefined,
    timestamp: undefined,
  },
  isErc20WithdrawalFeeCharging: false,
  isZrc2WithdrawalFeeCharging: false,
}

type State = {
  withdrawWalletId: WithdrawWalletIdType
  withdrawAccountId: string | undefined
  withdrawWalletName: string | number | undefined
  withdrawToBankId: number | undefined
  withdrawAmount: string
  withdrawBankAccount: string
  isConfirmOpen: boolean
  withdrawStep: WITHDRAW_STEPS
  is2FaOpen: boolean
  has2FaEnabled: boolean
  isLoading: boolean
  accountLimits: AccountLimits
  bankAccounts: BankAccount[]
  isSelectedBlockchain: boolean
  isAddressLoading: boolean
  isErc20FeatureGateLoading: boolean
  isZrc2FeatureGateLoading: boolean
  isBlockchainWithdrawEnabled: boolean
  blockchainAddresses: BlockchainAddress[]
  withdrawAmountError: string
  withdrawRequestError: string
  fullyVerified: boolean | undefined
  idMinWithdrawal: string
  idMaxWithdrawal: string
  fiList: FinancialInstituition[]
  accounts: { [key: string]: Account } | null
  isErc20Enabled: boolean
  isZrc2Enabled: boolean
  addressQuota: number
  isZrc2NoticeBannerVisible: boolean
  isStablecoinFeeLoading: boolean
  stablecoinFee: {
    amount: number | undefined
    identifier: string | undefined
    timestamp: string | undefined
  }
  isErc20WithdrawalFeeCharging: boolean
  isZrc2WithdrawalFeeCharging: boolean
}

export const withdraw: Reducer<State, ActionTypes> = (state = initialState, action) => {
  switch (action.type) {
    case WITHDRAW_ACTIONS.SET_WITHDRAW_WALLET: {
      return {
        ...state,
        withdrawWalletId: action.walletId,
        withdrawAccountId: action.accountId,
        withdrawWalletName: action.walletName,
      }
    }

    case WITHDRAW_ACTIONS.SET_IS_SELECTED_BLOCKCHAIN: {
      return {
        ...state,
        isSelectedBlockchain: action.isSelectedBlockchain,
      }
    }

    case WITHDRAW_ACTIONS.SET_WITHDRAW_AMOUNT: {
      return {
        ...state,
        withdrawAmount: action.amount,
        withdrawAmountError: action.withdrawAmountError,
      }
    }

    case WITHDRAW_ACTIONS.SET_WITHDRAW_TO_BANK_ID: {
      return {
        ...state,
        withdrawToBankId: action.bankId,
      }
    }

    case WITHDRAW_ACTIONS.SET_CONFIRM_MODAL_OPEN: {
      return {
        ...state,
        isConfirmOpen: action.open,
      }
    }

    case WITHDRAW_ACTIONS.SET_WITHDRAW_STEP: {
      return {
        ...state,
        withdrawStep: action.step,
      }
    }

    case WITHDRAW_ACTIONS.SET_2_FA_MODAL_OPEN: {
      return {
        ...state,
        is2FaOpen: action.open,
      }
    }

    case WITHDRAW_ACTIONS.SET_IS_LOADING: {
      return {
        ...state,
        isLoading: action.isLoading,
      }
    }
    case WITHDRAW_ACTIONS.GET_ACCOUNT_LIMITS: {
      return {
        ...state,
        accountLimits: action.accountLimits,
        isLoading: false,
      }
    }

    case WITHDRAW_ACTIONS.GET_USER_DETAILS: {
      return {
        ...state,
        bankAccounts: action.bankAccounts,
        has2FaEnabled: action.has2FaEnabled,
        fullyVerified: action.fullyVerified,
        isLoading: false,
      }
    }

    case WITHDRAW_ACTIONS.CREATE_WITHDRAWAL_REQUEST: {
      return {
        ...state,
        withdrawRequestError: action.error,
        withdrawBankAccount: action.withdrawBankAccount,
        isLoading: false,
      }
    }

    case WITHDRAW_ACTIONS.GET_ID_WITHDRAWAL_LIMITS: {
      return {
        ...state,
        idMinWithdrawal: action.idMinWithdrawal,
        idMaxWithdrawal: action.idMaxWithdrawal,
        isLoading: false,
      }
    }

    case WITHDRAW_ACTIONS.SET_IS_ADDRESS_LOADING: {
      return {
        ...state,
        isAddressLoading: action.isAddressLoading,
      }
    }

    case WITHDRAW_ACTIONS.SET_IS_ERC20_FEATURE_GATE_LOADING: {
      return {
        ...state,
        isErc20FeatureGateLoading: action.isErc20FeatureGateLoading,
      }
    }

    case WITHDRAW_ACTIONS.SET_IS_ZRC2_FEATURE_GATE_LOADING: {
      return {
        ...state,
        isZrc2FeatureGateLoading: action.isZrc2FeatureGateLoading,
      }
    }

    case WITHDRAW_ACTIONS.SET_BLOCKCHAIN_ADDRESSES: {
      return {
        ...state,
        blockchainAddresses: action.blockchainAddresses,
        fiList: action.fiList,
        addressQuota: action.addressQuota,
        isAddressLoading: false,
      }
    }

    case WITHDRAW_ACTIONS.SET_IS_BLOCKCHAIN_WITHDRAW_ENABLED: {
      return {
        ...state,
        isBlockchainWithdrawEnabled: action.isBlockchainWithdrawEnabled,
      }
    }

    case WITHDRAW_ACTIONS.SET_ACCOUNTS: {
      return {
        ...state,
        accounts: action.accounts,
      }
    }

    case WITHDRAW_ACTIONS.SET_IS_ERC20_ENABLED: {
      return {
        ...state,
        isErc20Enabled: action.isErc20Enabled,
      }
    }

    case WITHDRAW_ACTIONS.SET_IS_ZRC2_ENABLED: {
      return {
        ...state,
        isZrc2Enabled: action.isZrc2Enabled,
      }
    }

    case WITHDRAW_ACTIONS.SET_IS_ZRC2_NOTICE_BANNER_VISIBLE: {
      return {
        ...state,
        isZrc2NoticeBannerVisible: action.isZrc2NoticeBannerVisible,
      }
    }

    case WITHDRAW_ACTIONS.SET_IS_STABLECOIN_FEE_LOADING: {
      return {
        ...state,
        isStablecoinFeeLoading: action.isStablecoinFeeLoading,
      }
    }

    case WITHDRAW_ACTIONS.SET_STABLECOIN_FEE: {
      return {
        ...state,
        isStablecoinFeeLoading: false,
        stablecoinFee: {
          amount: action.amount,
          identifier: action.identifier,
          timestamp: action.timestamp,
        },
      }
    }

    case WITHDRAW_ACTIONS.SET_IS_ERC20_WITHDRAWAL_FEE_CHARGING: {
      return {
        ...state,
        isErc20WithdrawalFeeCharging: action.isErc20WithdrawalFeeCharging,
      }
    }

    case WITHDRAW_ACTIONS.SET_IS_ZRC2_WITHDRAWAL_FEE_CHARGING: {
      return {
        ...state,
        isZrc2WithdrawalFeeCharging: action.isZrc2WithdrawalFeeCharging,
      }
    }

    default:
      return state
  }
}
